import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const { FiHelpCircle, FiSend, FiMessageSquare, FiCheck, FiClock, FiAlertCircle } = FiIcons;

export default function Support() {
  const { user, familyData } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  useEffect(() => {
    if (user) loadTickets();
  }, [user]);

  const loadTickets = async () => {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    
    setSending(true);
    setStatusMsg(null);
    
    try {
      const { error } = await supabase.from('support_tickets').insert({
        user_id: user.id,
        family_id: familyData?.id,
        subject: newTicket.subject,
        message: newTicket.message,
        status: 'open'
      });

      if (error) throw error;

      setNewTicket({ subject: '', message: '' });
      setStatusMsg({ type: 'success', text: 'Anfrage erfolgreich gesendet!' });
      loadTickets();
    } catch (error) {
      setStatusMsg({ type: 'error', text: 'Fehler beim Senden: ' + error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center space-x-3 mb-8">
        <div className="bg-blue-100 p-3 rounded-xl">
          <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-blue-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Support & Hilfe</h1>
          <p className="text-gray-500">Wir helfen Ihnen gerne bei Problemen oder Fragen.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Create Ticket */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-fit">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <SafeIcon icon={FiSend} className="mr-2 text-blue-500" /> Neue Anfrage
          </h2>
          
          {statusMsg && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${statusMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {statusMsg.text}
            </div>
          )}

          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
              <input
                type="text"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Worum geht es?"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
              <textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]"
                placeholder="Beschreiben Sie Ihr Anliegen..."
                required
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {sending ? 'Wird gesendet...' : 'Absenden'}
            </button>
          </form>
        </div>

        {/* Ticket History */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <SafeIcon icon={FiClock} className="mr-2 text-gray-500" /> Verlauf
          </h2>
          
          {loading ? (
             <div className="text-center py-8 text-gray-400">Lade Verlauf...</div>
          ) : tickets.length === 0 ? (
             <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-400">
                Keine Anfragen bisher.
             </div>
          ) : (
             tickets.map(ticket => (
               <div key={ticket.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 transition-shadow hover:shadow-md">
                 <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900">{ticket.subject}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 
                      ticket.status === 'replied' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ticket.status === 'open' ? 'Offen' : ticket.status === 'replied' ? 'Beantwortet' : 'Geschlossen'}
                    </span>
                 </div>
                 <p className="text-gray-600 text-sm mb-3 whitespace-pre-wrap">{ticket.message}</p>
                 <div className="text-xs text-gray-400 mb-3">{format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}</div>
                 
                 {ticket.admin_reply && (
                   <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mt-2">
                     <div className="flex items-center text-blue-800 font-bold text-sm mb-1">
                       <SafeIcon icon={FiMessageSquare} className="w-4 h-4 mr-2" /> Support Antwort:
                     </div>
                     <p className="text-blue-900 text-sm whitespace-pre-wrap">{ticket.admin_reply}</p>
                   </div>
                 )}
               </div>
             ))
          )}
        </div>
      </div>
    </div>
  );
}